import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import dateFormat from 'dateformat';
import { get, capitalize, noop } from 'lodash-es';
import { EditorialEntities } from 'client/data/models/editorial';
import Card from 'reactstrap/lib/Card';
import CardText from 'reactstrap/lib/CardText';
import CardBody from 'reactstrap/lib/CardBody';
import CardTitle from 'reactstrap/lib/CardTitle';
import CardSubtitle from 'reactstrap/lib/CardSubtitle';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { NO_VEHICLE_GRAY_IMAGE_SEDAN } from 'site-modules/shared/constants/images';
import { isParsableDate } from 'site-modules/shared/utils/date-utils';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { Link } from 'site-modules/shared/components/link/link';
import { Image } from 'site-modules/shared/components/image/image';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { VideoPreview } from 'site-modules/shared/components/video-preview/video-preview';

import './article-card.scss';

function ArticleCardImg({ src = '', title = '', videoId = '', openVideoModal = noop }) {
  const defaultImg = getStaticImageUrl(NO_VEHICLE_GRAY_IMAGE_SEDAN);

  return (
    <AspectRatioContainer aspectRatio="16:9" className="video-card-img rounded mb-1 mb-md-0">
      {videoId ? (
        <VideoPreview
          videoId={videoId}
          openVideoModal={openVideoModal}
          videoTitle={title}
          creativeId="other_about_us_author_index"
          isStaticPos
          isAutoHeight
          isWhiteBtn
        />
      ) : (
        <Image imageId={src || defaultImg} alt={title} source={''} errorImage={defaultImg} className="w-100 mw-100" />
      )}
    </AspectRatioContainer>
  );
}

ArticleCardImg.propTypes = {
  src: PropTypes.string,
  title: PropTypes.string,
  videoId: PropTypes.string,
  openVideoModal: PropTypes.func,
};

function ArticleCardHeader({ title = '', url = '', date = '', type = '', typeNews = false }) {
  const tagTitle = `${type.charAt(0).toUpperCase() + type.slice(1)}`;
  const typeTitle = `${tagTitle.replace('Ev', 'EV')} News`;
  return (
    <CardBody className="px-0">
      <CardTitle tag="h2" className="article-card-title text-gray-darker fw-bold">
        <Link to={url}>
          <ContentFragment componentToUse="span">{title}</ContentFragment>
        </Link>
      </CardTitle>
      <CardSubtitle className="text-gray-dark d-flex justify-content-between align-items-center">
        {date && isParsableDate(date) && typeNews && (
          <p className="size-14">
            {dateFormat(date, 'mmm d, yyyy')} | {typeTitle}
          </p>
        )}
        {date && isParsableDate(date) && !typeNews && <p className="size-14">{dateFormat(date, 'mmm d, yyyy')}</p>}
      </CardSubtitle>
    </CardBody>
  );
}

ArticleCardHeader.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string,
  date: PropTypes.string,
  typeNews: PropTypes.bool,
  type: PropTypes.string,
};

function ArticleCardBody({
  summary = '',
  researchCategorySlug = '',
  researchCategoryName = '',
  authorName = '',
  hideCategory = false,
  hasAuthorLink = true,
}) {
  return (
    <CardBody className="px-0">
      {!hideCategory && (
        <p className="text-end">
          <Button
            tag={Link}
            color="blue-50"
            to={`/${researchCategorySlug}/`}
            className="size-14 text-transform-none px-0_5 py-0_25"
          >
            {researchCategoryName}
          </Button>
        </p>
      )}
      <CardText className="article-summary text-gray-darker size-16">{summary}</CardText>
      <CardText className="size-14 text-gray-darker">
        {!!authorName && (
          <Fragment>
            {hasAuthorLink ? (
              <Fragment>
                By{' '}
                <Link to={`/about/authors/${makeNiceName(authorName)}.html`} rel="nofollow">
                  {authorName}
                </Link>
              </Fragment>
            ) : (
              `By ${authorName}`
            )}
          </Fragment>
        )}
      </CardText>
    </CardBody>
  );
}

ArticleCardBody.propTypes = {
  summary: PropTypes.string,
  researchCategorySlug: PropTypes.string,
  researchCategoryName: PropTypes.string,
  authorName: PropTypes.string,
  hideCategory: PropTypes.bool,
  hasAuthorLink: PropTypes.bool,
};

const MMY_REVIEW_URL_PATTERN = /\/(.+)\/.+\/\d\d\d\d/;

export function ArticleCard({
  article = {},
  authorName = '',
  hideCategory = false,
  typeNews = false,
  type = '',
  hasAuthorLink = true,
  openVideoModal = noop,
  isMobile = false,
}) {
  const {
    title = '',
    image = '',
    relativeUrl = '',
    videoId = '',
    summary = '',
    publishDate = '',
    updateDate = '',
    isPlaceholder,
  } = article;

  const isLoading = isPlaceholder;

  if (!isLoading && !title) return null;

  const isMmyReviewUrl = MMY_REVIEW_URL_PATTERN.test(relativeUrl);

  let researchCategorySlug;
  let researchCategoryName;

  if (videoId) {
    researchCategorySlug = 'car-news';
    researchCategoryName = 'Car News';
  } else if (isMmyReviewUrl) {
    researchCategorySlug = 'car-reviews';
    researchCategoryName = 'Car Reviews';
  } else {
    researchCategorySlug = get(relativeUrl.split('/'), [1], '');
    researchCategoryName = researchCategorySlug
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => capitalize(word))
      .join(' ');
  }

  const date = updateDate || publishDate;
  const cardUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : relativeUrl;

  return (
    <Card className="article-card d-block h-100 pb-md-1_5 mb-md-1_5">
      <LoadingSpinner isLoading={isLoading}>
        {isMobile ? (
          <div className="px-0_5 py-1_5">
            <ArticleCardHeader title={title} url={cardUrl} date={date} typeNews={typeNews} type={type} />
            <ArticleCardImg title={title} src={image} videoId={videoId} openVideoModal={openVideoModal} />
            <ArticleCardBody
              summary={summary}
              researchCategorySlug={researchCategorySlug}
              researchCategoryName={researchCategoryName}
              authorName={authorName}
              url={cardUrl}
              hideCategory={hideCategory}
              hasAuthorLink={hasAuthorLink}
            />
          </div>
        ) : (
          <Row>
            <Col xs={6} className="py-1_5">
              <ArticleCardHeader title={title} url={cardUrl} date={date} typeNews={typeNews} type={type} />
              <ArticleCardBody
                summary={summary}
                researchCategorySlug={researchCategorySlug}
                researchCategoryName={researchCategoryName}
                authorName={authorName}
                url={cardUrl}
                hideCategory={hideCategory}
                hasAuthorLink={hasAuthorLink}
              />
            </Col>
            <Col xs={6}>
              <ArticleCardImg title={title} src={image} videoId={videoId} openVideoModal={openVideoModal} />
            </Col>
          </Row>
        )}
      </LoadingSpinner>
    </Card>
  );
}

ArticleCard.propTypes = {
  article: EditorialEntities.Article,
  authorName: PropTypes.string,
  hideCategory: PropTypes.bool,
  typeNews: PropTypes.bool,
  type: PropTypes.string,
  openVideoModal: PropTypes.func,
  hasAuthorLink: PropTypes.bool,
  isMobile: PropTypes.bool,
};
